import { CircleAlert, CircleCheck, X } from "lucide-react";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { useToast } from "@/app/providers/ToastProvider";
import { ROUTES } from "@/app/routes";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { EmptyState, ErrorState } from "@/components/common/EmptyState";
import { Loader } from "@/components/common/Loader";
import { LoadingOverlay } from "@/components/common/LoadingOverlay";
import { SearchInput } from "@/components/common/SearchInput";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { useAccounts } from "@/features/accounts/hooks/useAccounts";
import { useCurrency } from "@/features/accounts/hooks/useCurrency";
import { useProductStock, useProductStocks } from "@/features/product-stocks/hooks/useProductStocks";
import type { Vending } from "@/features/vending/api/vendingApi";
import { addToCart, cartTotal, changeQuantity, type CartLine } from "@/features/vending/cart";
import { CartPanel } from "@/features/vending/components/CartPanel";
import { EditVendingDialog } from "@/features/vending/components/EditVendingDialog";
import { ProductGrid } from "@/features/vending/components/ProductGrid";
import { SalesHistory } from "@/features/vending/components/SalesHistory";
import { useCreateVending, useDeleteVending, useVendings } from "@/features/vending/hooks/useVending";
import { toLocalDateTime } from "@/lib/datetime";
import { formatMoney } from "@/lib/format";
import { toErrorMessage } from "@/lib/swr";
import { cn } from "@/lib/utils";

type ActionNotice = {
  tone: "success" | "warning" | "danger";
  title: string;
  message: string;
};

export default function VendingPage() {
  const [showHistory, setShowHistory] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [lines, setLines] = useState<CartLine[]>([]);
  const [label, setLabel] = useState("");
  const [received, setReceived] = useState("");
  const [accountId, setAccountId] = useState("");
  const [editingSale, setEditingSale] = useState<Vending | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Vending | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [checkoutProgress, setCheckoutProgress] = useState<{ current: number; total: number } | null>(null);
  const [checkoutStep, setCheckoutStep] = useState<"selection" | "payment">("selection");
  const [actionNotice, setActionNotice] = useState<ActionNotice | null>(null);
  const toast = useToast();

  const { currency } = useCurrency();
  const { accounts } = useAccounts();
  const { stocks, isLoading: stocksLoading, error: stocksError, refresh: refreshStocks } = useProductStocks();
  const stockId = stocks[0]?.id;
  const { stock, isLoading: stockLoading, error: stockError, refresh } = useProductStock(stockId);
  const { vendings, refresh: refreshVendings } = useVendings();
  const { create, isCreating } = useCreateVending();
  const { remove, isDeleting } = useDeleteVending();

  const selectedAccount = accountId || accounts[0]?.id || "";
  const total = useMemo(() => cartTotal(lines), [lines]);

  const items = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const all = stock?.items ?? [];
    return term ? all.filter((item) => item.product.name.toLowerCase().includes(term)) : all;
  }, [stock, searchTerm]);

  /**
   * A sale carries its product name, which the merchant stock cannot: a sold
   * out or deleted product is no longer one of its items.
   */
  const productName = (vending: Vending) =>
    vending.productName ||
    stock?.items.find((item) => item.product.id === vending.productId)?.product.name ||
    "Produit";

  const handleDeleteSale = async () => {
    if (!pendingDelete) return;
    setDeleteError(null);
    try {
      const deleted = await remove(pendingDelete.id);
      const message = `Solde ramené à ${formatMoney(deleted.accountBalance, currency)}.`;
      toast.success(`Vente annulée — ${message}`);
      setActionNotice({ tone: "success", title: "Vente annulée", message });
      setPendingDelete(null);
    } catch (e) {
      const message = toErrorMessage(e) ?? "La vente n’a pas pu être annulée.";
      toast.error(message);
      setActionNotice({ tone: "danger", title: "Annulation impossible", message });
      setDeleteError(`Annulation impossible. ${message} Vous pouvez réessayer ou fermer cette fenêtre.`);
    }
  };

  const handleCheckout = async () => {
    if (!selectedAccount) {
      toast.error("Aucun compte d’encaissement disponible.");
      return;
    }
    if (!stockId) return;

    const vendedAt = toLocalDateTime(DateTime.now());
    const saleLabel = label.trim() || `Vente de ${DateTime.now().toFormat("dd/MM/yyyy")}`;
    const soldTotal = total;
    const soldLines: CartLine[] = [];
    setCheckoutProgress({ current: 0, total: lines.length });

    try {
      // The API records one sale per product, so a basket is several calls.
      for (const [index, line] of lines.entries()) {
        setCheckoutProgress({ current: index + 1, total: lines.length });
        await create({
          productStockId: stockId,
          productId: line.productId,
          quantity: line.quantity,
          label: saleLabel,
          vendedAt,
          accountId: selectedAccount,
          price: line.unitPrice,
        });
        soldLines.push(line);
      }
      toast.success(`Vente encaissée — ${formatMoney(soldTotal, currency)}.`);
      setActionNotice({
        tone: "success",
        title: "Vente enregistrée",
        message: `${formatMoney(soldTotal, currency)} encaissés.`,
      });
      setLines([]);
      setCheckoutStep("selection");
      setLabel("");
      setReceived("");
      await Promise.all([refresh(), refreshVendings()]);
    } catch (e) {
      // Keep only lines that are not known to have been persisted, so retrying
      // does not resend earlier successful calls from the same batch.
      if (soldLines.length > 0) {
        const soldProductIds = new Set(soldLines.map((line) => line.productId));
        setLines((current) => current.filter((line) => !soldProductIds.has(line.productId)));
        toast.error(
          `${soldLines.length} ${soldLines.length > 1 ? "lignes ont été enregistrées" : "ligne a été enregistrée"}. ` +
            `Le reste du panier est conservé : ${toErrorMessage(e) ?? "réessayez."}`,
        );
        setActionNotice({
          tone: "warning",
          title: "Vente partiellement enregistrée",
          message:
            `${soldLines.length} ${soldLines.length > 1 ? "lignes ont été enregistrées" : "ligne a été enregistrée"}. ` +
            "Le reste du panier est conservé pour être réessayé.",
        });
      } else {
        const message = toErrorMessage(e) ?? "La vente n’a pas pu être encaissée.";
        toast.error(message);
        setActionNotice({ tone: "danger", title: "Vente non enregistrée", message });
      }
    } finally {
      setCheckoutProgress(null);
    }
  };

  const isLoading = stocksLoading || stockLoading;
  const isCheckingOut = isCreating || checkoutProgress !== null;

  return (
    <div className="flex flex-col gap-5">
      {isCheckingOut && (
        <LoadingOverlay
          message={
            checkoutProgress
              ? `Encaissement de la ligne ${checkoutProgress.current} sur ${checkoutProgress.total}…`
              : "Encaissement…"
          }
        />
      )}

      <PageHeader
        title={showHistory ? "Ventes du jour" : "Caisse"}
        subtitle={
          showHistory
            ? "Tout ce qui a été encaissé, ligne par ligne"
            : checkoutStep === "selection"
              ? "Touchez un produit pour l’ajouter au panier"
              : "Vérifiez le panier et encaissez la vente"
        }
        action={
          <Button variant="outline" size="lg" onClick={() => setShowHistory((v) => !v)}>
            {showHistory ? "Retour à la caisse" : "Historique"}
          </Button>
        }
      />

      {actionNotice ? <ActionNotice notice={actionNotice} onDismiss={() => setActionNotice(null)} /> : null}

      {showHistory ? (
        <SalesHistory
          vendings={vendings}
          currency={currency}
          onEdit={setEditingSale}
          onDelete={(sale) => {
            setDeleteError(null);
            setPendingDelete(sale);
          }}
        />
      ) : (
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
          {checkoutStep === "selection" ? (
            <div className="flex min-w-0 flex-1 flex-col gap-4">
              <SearchInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un produit…"
              />

              {(stocksLoading || (stockId && stockLoading)) && !stocksError && !stockError ? <Loader /> : null}

              {!stocksLoading && !stocksError && stocks.length === 0 ? (
                <EmptyState
                  title="Aucun stock marchand"
                  hint="Créez ou sélectionnez un stock marchand avant d’ouvrir la caisse."
                  action={
                    <Button asChild variant="outline">
                      <Link to={ROUTES.productStocks}>Voir les stocks marchands</Link>
                    </Button>
                  }
                />
              ) : null}

              {stocksError ? <ErrorState message={stocksError} onRetry={() => refreshStocks()} /> : null}
              {!stocksError && stockError ? <ErrorState message={stockError} onRetry={() => refresh()} /> : null}

              {!stocksLoading && !stockLoading && stock && !stocksError && !stockError && items.length === 0 && (
                <EmptyState
                  title={searchTerm ? "Aucun produit ne correspond" : "Aucun produit en stock"}
                  hint={
                    searchTerm
                      ? "Essayez un autre nom ou effacez la recherche."
                      : "Produisez depuis vos matières premières pour pouvoir vendre."
                  }
                  action={
                    searchTerm ? (
                      <Button type="button" variant="outline" onClick={() => setSearchTerm("")}>
                        Effacer la recherche
                      </Button>
                    ) : (
                      <Button asChild variant="outline">
                        <Link to={ROUTES.products}>Gérer les produits</Link>
                      </Button>
                    )
                  }
                />
              )}

              {!isLoading && items.length > 0 && (
                <ProductGrid
                  items={items}
                  currency={currency}
                  onPick={(item) => setLines((prev) => addToCart(prev, item))}
                />
              )}
            </div>
          ) : (
            <div className="flex min-h-48 min-w-0 flex-1 items-center justify-center rounded-xl border border-dashed border-border bg-muted/30 p-6 text-center">
              <div className="max-w-sm">
                <p className="font-semibold">Vérifiez le panier avant d’encaisser</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Le stock reste masqué pendant le paiement pour garder votre attention sur le montant et le compte.
                </p>
              </div>
            </div>
          )}

          <CartPanel
            lines={lines}
            total={total}
            currency={currency}
            label={label}
            onLabelChange={setLabel}
            accounts={accounts}
            accountId={selectedAccount}
            onAccountChange={setAccountId}
            received={received}
            onReceivedChange={setReceived}
            onChangeQuantity={(productId, delta) => setLines((prev) => changeQuantity(prev, productId, delta))}
            onRemove={(productId) => {
              if (lines.length <= 1) setCheckoutStep("selection");
              setLines((prev) => prev.filter((line) => line.productId !== productId));
            }}
            onClear={() => {
              setLines([]);
              setCheckoutStep("selection");
            }}
            onCheckout={handleCheckout}
            onContinue={() => setCheckoutStep("payment")}
            onBackToSelection={() => setCheckoutStep("selection")}
            step={checkoutStep}
            isSubmitting={isCheckingOut}
          />
        </div>
      )}

      {editingSale && (
        <EditVendingDialog
          vending={editingSale}
          currency={currency}
          productName={productName(editingSale)}
          onClose={() => setEditingSale(null)}
          onSaved={() => setEditingSale(null)}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          title="Annuler cette vente ?"
          description={`${productName(pendingDelete)} — ${formatMoney(
            pendingDelete.price * pendingDelete.quantity,
            currency,
          )}`}
          details="Le montant sera retiré du solde du compte et la marchandise remise en stock. La vente reste archivée."
          confirmLabel="Annuler la vente"
          isPending={isDeleting}
          error={deleteError}
          onConfirm={handleDeleteSale}
          onCancel={() => {
            setDeleteError(null);
            setPendingDelete(null);
          }}
        />
      )}
    </div>
  );
}

function ActionNotice({ notice, onDismiss }: { notice: ActionNotice; onDismiss: () => void }) {
  const Icon = notice.tone === "success" ? CircleCheck : CircleAlert;

  return (
    <div
      role={notice.tone === "danger" ? "alert" : "status"}
      aria-live="polite"
      className={cn(
        "flex items-start gap-3 rounded-xl border px-4 py-3",
        notice.tone === "success" && "border-success/30 bg-success-soft text-success-foreground",
        notice.tone === "warning" && "border-warning/30 bg-warning-soft text-warning-foreground",
        notice.tone === "danger" && "border-destructive/30 bg-destructive-soft text-destructive",
      )}
    >
      <Icon aria-hidden="true" size={20} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{notice.title}</p>
        <p className="mt-0.5 text-sm leading-relaxed">{notice.message}</p>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="grid h-9 w-9 shrink-0 place-items-center rounded-lg transition hover:bg-black/5"
        aria-label="Masquer le message"
      >
        <X size={17} />
      </button>
    </div>
  );
}
